import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import {
  loadHdChartGlobalSettings,
  saveHdChartGlobalSettings,
} from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HdMomentLinkDialogComponent, HdMomentLinkTarget } from '@/app/human-design/chart/bodygraph/advanced-properties/hd-moment-link-dialog.component';
import {
  HD_MOMENT_CATEGORIES,
  HD_MOMENT_CATEGORIES_HIDDEN_ON_TIMELINE,
  hdMomentCategoryLabelKey,
} from '@/app/human-design/chart/bodygraph/advanced-properties/hd-moment-fields';
import { buildHdLifeTimelineMarkers, buildHdMomentRows } from '@/app/human-design/chart/bodygraph/advanced-properties/hd-moments.models';
import { HD_PLANET_ORDER_STANDARD, HdPlanetKey, getHdPlanetDisplayConfig } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { TransitDataService } from '@/app/human-design/transits/transit-data.service';
import {
  HD_TRANSIT_LEGEND_CATEGORY_ORDER,
  HD_TRANSIT_LEGEND_LABEL_KEYS,
  HdTransitLegendCategory,
  legendCategoryOfKey,
} from '@/app/human-design/transits/transit-legend.utils';
import { HdBodygraphChartPreset } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { buildChartDataSourceQuery } from '@/app/human-design/chart/library/hd-chart-datasource-query';
import { hdCycleLabel } from '@/app/human-design/chart/library/hd-chart-cycle-metadata';
import { HdChartCyclesService } from '@/app/human-design/chart/library/hd-chart-cycles.service';
import { HdChartGroupService } from '@/app/human-design/chart/library/hd-chart-group.service';
import { HdChartLibraryService } from '@/app/human-design/chart/library/hd-chart-library.service';
import { HdChartEditDialogComponent } from '@/app/human-design/chart/library/hd-chart-edit-dialog.component';
import { HdChartDocumentResult, HdChartUpdateInput } from '@/app/human-design/chart/library/hd-chart-library.models';
import { EgiPageNavigationService } from '@/app/platform/platform-context/egi-page-navigation.service';
import { HD_CHART_ENVIRONMENT_PAGE_ID } from '../hd-page-ids';
import { HdLifeTimelineComponent } from '@/app/human-design/chart/timeline/hd-life-timeline.component';
import { HdMomentsCalendarComponent } from '@/app/human-design/chart/timeline/hd-moments-calendar.component';
import { HdIChingAccessService } from '@/app/human-design/iching/hd-iching-access.service';
import { HdIChingMomentDialogComponent } from '@/app/human-design/iching/moment/hd-iching-moment-dialog.component';
import {
  HdMomentsExportDialogComponent,
  HdMomentsExportTarget,
} from '@/app/human-design/chart/moments-export/hd-moments-export-dialog.component';
import { HdIChingConsultRequest, HdIChingMomentDialogTarget } from '@/app/human-design/iching/moment/hd-iching-moment.models';
import { HdIChingMomentsService } from '@/app/human-design/iching/moment/hd-iching-moments.service';
import {
  HdLifeTimelineMarker,
  HdLifeTimelineViewport,
  HdLifeTransitBlock,
  HdLifeTransitLane,
} from '@/app/human-design/chart/timeline/hd-life-timeline.models';

/** Strip-datachunks: ruim onder de backend-cap van 5 jaar per gate-call. */
const STRIP_CHUNK_MS = 4 * 365.25 * 24 * 3_600_000;

/**
 * Levenstijdlijn-pagina (spec momenten stap 5): route
 * `human-design/life-timeline?chartId=…`. Toont per gekozen chart alle
 * expliciete moment- en cycle-links als tijdlijn (stippen, range-balkjes) plus
 * de berekende cycli als systeem-markers; lagen zijn schakelbaar. Openen gaat
 * naar de chart-omgeving (`?loadChartId=`), toevoegen via de moment-dialog met
 * de aangeklikte datum voorgevuld.
 */
@Component({
  selector: 'hd-life-timeline-page',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    NgSelectModule,
    HdLifeTimelineComponent,
    HdMomentsCalendarComponent,
    HdMomentLinkDialogComponent,
    HdChartEditDialogComponent,
    HdIChingMomentDialogComponent,
    HdMomentsExportDialogComponent,
  ],
  templateUrl: './hd-life-timeline-page.component.html',
  styleUrl: './hd-life-timeline-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLifeTimelinePageComponent {
  private readonly library = inject(HdChartLibraryService);
  private readonly groupService = inject(HdChartGroupService);
  private readonly cyclesService = inject(HdChartCyclesService);
  private readonly transitData = inject(TransitDataService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly egiPageNavigation = inject(EgiPageNavigationService);
  private readonly ichingMoments = inject(HdIChingMomentsService);
  protected readonly ichingAccess = inject(HdIChingAccessService);

  /** Kandidaten voor de chart-kiezer: eerste 100 Standard-charts (spec levenstijdlijn §2.6). */
  protected readonly candidates = signal<PlatformChartListItemDto[]>([]);
  protected readonly chartId = signal<string | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  /** Deeplink naar een niet-Standard chart: tijdlijn geblokkeerd met melding. */
  protected readonly standardBlocked = signal(false);

  /** Mini-labels op de tijdlijn (spec §2.8), gepersisteerd in de settings-blob. */
  protected readonly showLabels = signal(loadHdChartGlobalSettings().lifeTimelineLabels);
  /** Kleurmodus momentkleur | stemming (spec §2.3), gepersisteerd. */
  protected readonly colorMode = signal(loadHdChartGlobalSettings().lifeTimelineColorMode);

  /**
   * Categoriefilter (spec §2.7): uitgevinkte categorieën verdwijnen van de
   * lijn; daily staat default uit (beslispunt 9 — de levenstijdlijn is de
   * lifetime-focus). Momenten zonder categorie volgen 'lifetime'.
   */
  protected readonly hiddenCategories = signal<ReadonlySet<string>>(new Set(HD_MOMENT_CATEGORIES_HIDDEN_ON_TIMELINE));
  protected readonly categories = HD_MOMENT_CATEGORIES;

  /** Categorie-labels in de content-taal (zelfde patroon als text()). */
  protected readonly categoryLabels = computed<Record<string, string>>(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    return Object.fromEntries(HD_MOMENT_CATEGORIES.map(category => [
      category,
      this.transloco.translate<string>(hdMomentCategoryLabelKey(category), undefined, language),
    ]));
  });

  private readonly chartPreset = signal<HdBodygraphChartPreset | null>(null);
  protected readonly momentMarkers = signal<HdLifeTimelineMarker[]>([]);
  private readonly cycleMarkers = signal<HdLifeTimelineMarker[]>([]);
  /** I Tjing-momenten (raadplegingen uit het dagboek bij deze chart); leeg zonder toegang. */
  private readonly ichingMarkers = signal<HdLifeTimelineMarker[]>([]);
  private ichingLoadSeq = 0;
  /** Laag "I Tjing" (enkel zichtbaar met toegang), default aan. */
  protected readonly showIChing = signal(true);
  /** Doel van de I Tjing-momentdialoog (lezen of nieuw); null = gesloten. */
  protected readonly ichingDialogTarget = signal<HdIChingMomentDialogTarget | null>(null);
  /** Markdown-export van alle momenten van de gekozen chart (spec 2026-10-21-dagboek-momenten-export-design). */
  protected readonly exportTarget = signal<HdMomentsExportTarget | null>(null);

  /** Weergave van de pagina: tijdlijn of kalender (beslispunt 10), gepersisteerd. */
  protected readonly view = signal(loadHdChartGlobalSettings().lifeTimelineView);

  // ── Transit-strip (spec §2.5) ──────────────────────────────────────────────
  /** Strip aan/uit, gepersisteerd; de planeetselectie is bewust sessie-only (zoals transits). */
  protected readonly showTransitStrip = signal(loadHdChartGlobalSettings().lifeTimelineTransitStrip);
  /** Eigen filterselectie, default de trage planeten (sociaal + transpersoonlijk). */
  protected readonly stripCategories = signal<ReadonlySet<HdTransitLegendCategory>>(
    new Set(['outer-social', 'outer-trans']));
  protected readonly stripLanes = signal<readonly HdLifeTransitLane[]>([]);
  private readonly lastViewport = signal<HdLifeTimelineViewport | null>(null);
  private stripLoadSeq = 0;

  protected readonly stripCategoryOrder = HD_TRANSIT_LEGEND_CATEGORY_ORDER;

  /** Strip aangevraagd maar de zoom is te grof: hintregel (spec §2.5). */
  protected readonly stripZoomTooCoarse = computed(() => {
    const viewport = this.lastViewport();
    return this.showTransitStrip() && viewport != null
      && viewport.zoom !== 'year' && viewport.zoom !== 'month';
  });

  /** Planeten in de strip: standaardvolgorde, gefilterd op de categorie-selectie. */
  private readonly stripPlanetKeys = computed<readonly HdPlanetKey[]>(() => {
    const categories = this.stripCategories();
    const keys: readonly HdPlanetKey[] = loadHdChartGlobalSettings().showChiron
      ? [...HD_PLANET_ORDER_STANDARD, 'chiron']
      : HD_PLANET_ORDER_STANDARD;
    return keys.filter(key => categories.has(legendCategoryOfKey(key)));
  });

  /** Lagen (beslispunt 2): elk aan/uit-schakelbaar. */
  protected readonly showMoments = signal(true);
  protected readonly showRanges = signal(true);
  protected readonly showCycles = signal(true);

  /** Doel + datum-prefill van de moment-dialog (toevoegen vanaf de as of de kalender). */
  protected readonly momentDialogTarget = signal<HdMomentLinkTarget | null>(null);
  protected readonly momentDialogPrefillUtc = signal<string | null>(null);
  /** Categorie-preselectie per ingang: tijdlijn = lifetime, kalender = daily (spec §2.7). */
  protected readonly momentDialogCategory = signal<string>('lifetime');

  constructor() {
    void this.loadCandidates();
    // Deeplink: ?chartId= volgt de kiezer en andersom.
    const initial = this.route.snapshot.queryParamMap.get('chartId');
    if (initial) this.chartId.set(initial);

    effect(() => {
      const chartId = this.chartId();
      untracked(() => {
        void this.router.navigate([], { queryParams: { chartId: chartId || null }, queryParamsHandling: 'merge', replaceUrl: true });
        this.resetData();
        if (chartId) void this.loadChart(chartId);
      });
    });

    // Strip-toggle of planeetselectie gewijzigd → segmenten (her)laden op het
    // laatst bekende venster.
    effect(() => {
      this.showTransitStrip();
      this.stripCategories();
      untracked(() => void this.loadStripSegments());
    });

    // Een raadpleging bewaard of verwijderd (dialoog), of de toegang wijzigde → I Tjing-markers herladen.
    effect(() => {
      this.ichingMoments.changed();
      this.ichingAccess.classicVisible();
      const chartId = this.chartId();
      untracked(() => void this.loadIChingMarkers(chartId));
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.lifeTimeline.title'),
      chartPlaceholder: translate('humanDesign.lifeTimeline.chartPlaceholder'),
      layerMoments: translate('humanDesign.lifeTimeline.layers.moments'),
      layerRanges: translate('humanDesign.lifeTimeline.layers.ranges'),
      layerCycles: translate('humanDesign.lifeTimeline.layers.cycles'),
      empty: translate('humanDesign.lifeTimeline.empty'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      standardOnly: translate('humanDesign.lifeTimeline.standardOnly'),
      noBirth: translate('humanDesign.lifeTimeline.noBirth'),
      viewTimeline: translate('humanDesign.lifeTimeline.viewTimeline'),
      viewCalendar: translate('humanDesign.lifeTimeline.viewCalendar'),
      export: translate('humanDesign.momentsExport.button'),
      exportTitle: translate('humanDesign.momentsExport.buttonTitle'),
      stripToggle: translate('humanDesign.lifeTimeline.stripToggle'),
      stripZoomHint: translate('humanDesign.lifeTimeline.stripZoomHint'),
      layerIChing: translate('humanDesign.lifeTimeline.layers.iching'),
    };
  });

  /** Vertaalde labels van de strip-filtercategorieën (transits-vocabulaire). */
  protected readonly stripCategoryLabels = computed<Record<string, string>>(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    return Object.fromEntries(HD_TRANSIT_LEGEND_CATEGORY_ORDER.map(category => [
      category,
      this.transloco.translate<string>(
        `humanDesign.chart.transits.panel.${HD_TRANSIT_LEGEND_LABEL_KEYS[category]}`, undefined, language),
    ]));
  });

  protected readonly chartOptions = computed(() =>
    this.candidates()
      .filter(chart => chart.id)
      .map(chart => ({ value: chart.id as string, label: chart.name ?? '' })));

  protected readonly birthUtc = computed(() => {
    const birth = (this.chartPreset()?.birthData ?? {}) as { time?: { utc?: string | null } | null };
    return birth.time?.utc ?? null;
  });

  protected readonly chartName = computed(() => this.chartPreset()?.name ?? '');

  /** Chart geladen maar zonder geboortemoment: nette melding i.p.v. stilte. */
  protected readonly loadedWithoutBirth = computed(() =>
    this.chartPreset() != null && !this.birthUtc());

  /** Momenten plus I Tjing-momenten (laag "I Tjing"); de kalender filtert categorieën zelf. */
  protected readonly calendarMarkers = computed<readonly HdLifeTimelineMarker[]>(() => [
    ...this.momentMarkers(),
    ...(this.showIChing() ? this.ichingMarkers() : []),
  ]);

  protected readonly visibleMarkers = computed<readonly HdLifeTimelineMarker[]>(() => {
    const hidden = this.hiddenCategories();
    return [
      ...this.momentMarkers()
        .filter(marker => (marker.kind === 'range' ? this.showRanges() : this.showMoments()))
        .filter(marker => !hidden.has(marker.category ?? 'lifetime')),
      // I Tjing-momenten volgen de eigen laag én het categoriefilter (daily, dus default uit op de lijn).
      ...(this.showIChing() ? this.ichingMarkers().filter(marker => !hidden.has(marker.category ?? 'daily')) : []),
      ...(this.showCycles() ? this.cycleMarkers() : []),
    ];
  });

  protected toggleCategoryFilter(category: string): void {
    const next = new Set(this.hiddenCategories());
    if (next.has(category)) next.delete(category);
    else next.add(category);
    this.hiddenCategories.set(next);
  }

  protected onChartChosen(chartId: string | null): void {
    this.chartId.set(chartId);
  }

  /** Labels-toggle vanuit de tijdlijn-toolbar: meteen persisteren. */
  protected onShowLabelsChange(value: boolean): void {
    this.showLabels.set(value);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), lifeTimelineLabels: value });
  }

  /** Kleurmodus-toggle (momentkleur | stemming): meteen persisteren. */
  protected onColorModeChange(value: 'moment' | 'mood'): void {
    this.colorMode.set(value);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), lifeTimelineColorMode: value });
  }

  /** Weergave-toggle tijdlijn | kalender (beslispunt 10): meteen persisteren. */
  protected openExport(): void {
    const chartId = this.chartId();
    if (!chartId) return;
    this.exportTarget.set({ kind: 'chart', chartId, chartName: this.chartName() });
  }

  protected onViewChange(value: 'timeline' | 'calendar'): void {
    if (this.view() === value) return;
    this.view.set(value);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), lifeTimelineView: value });
  }

  /** Transit-strip aan/uit: meteen persisteren (de planeetselectie is sessie-only). */
  protected onStripToggle(value: boolean): void {
    this.showTransitStrip.set(value);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), lifeTimelineTransitStrip: value });
  }

  protected toggleStripCategory(category: HdTransitLegendCategory): void {
    const next = new Set(this.stripCategories());
    if (next.has(category)) next.delete(category);
    else next.add(category);
    this.stripCategories.set(next);
  }

  /** Zichtbaar tijdvenster van de tijdlijn: hierop laadt de strip lazy zijn segmenten. */
  protected onViewportChanged(viewport: HdLifeTimelineViewport): void {
    this.lastViewport.set(viewport);
    void this.loadStripSegments();
  }

  /** Openen vanuit de kalender: chart-omgeving mét het Notities-paneel (spec §2.9). */
  protected openChartWithNotes(chartId: string): void {
    this.egiPageNavigation.navigateToPage(HD_CHART_ENVIRONMENT_PAGE_ID, { loadChartId: chartId, openNotes: '1' });
  }

  // ── Moment bewerken (finetune 2026-09-21) ──────────────────────────────────

  /** Doel van de editdialoog (geladen document); null = gesloten. */
  protected readonly editDialogTarget = signal<HdChartDocumentResult | null>(null);

  /** Potlood op tijdlijn-popover of kalender: laad het document en open de editdialoog. */
  protected async openMomentEdit(chartId: string): Promise<void> {
    this.error.set('');
    try {
      this.editDialogTarget.set(await this.library.getChartDocument(chartId));
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Opslaan uit de editdialoog: persisteren en de markers verversen. */
  protected async onMomentEditSaved(input: HdChartUpdateInput): Promise<void> {
    this.error.set('');
    try {
      await this.library.updateChart(input);
      const chartId = this.chartId();
      if (chartId) await this.loadChart(chartId);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** "+ moment" vanuit de kalender: dag voorgevuld en categorie daily (spec §2.7). */
  protected onCalendarAddRequested(dateUtc: string): void {
    const chartId = this.chartId();
    const name = this.chartName();
    if (!chartId || !name) return;
    this.momentDialogCategory.set('daily');
    this.momentDialogPrefillUtc.set(dateUtc);
    this.momentDialogTarget.set({ chartId, chartName: name });
  }

  /** Popover-Openen op de tijdlijn: laad de chart in de chart-omgeving (nieuwe tab daar). */
  protected openChart(chartId: string): void {
    this.egiPageNavigation.navigateToPage(HD_CHART_ENVIRONMENT_PAGE_ID, { loadChartId: chartId });
  }

  /** Klik op een leeg stuk as: moment-dialog met de aangeklikte dag voorgevuld (lifetime). */
  protected onAddRequested(dateUtc: string): void {
    const chartId = this.chartId();
    const name = this.chartName();
    if (!chartId || !name) return;
    this.momentDialogCategory.set('lifetime');
    this.momentDialogPrefillUtc.set(dateUtc);
    this.momentDialogTarget.set({ chartId, chartName: name });
  }

  protected onMomentLinkChanged(): void {
    const chartId = this.chartId();
    if (chartId) void this.loadChart(chartId);
  }

  /** Nieuw aangemaakt moment standaard openen (zelfde gedrag als in de Momenten-sectie). */
  protected onMomentChartCreated(newChartId: string): void {
    this.openChart(newChartId);
  }

  // ── I Tjing-momenten (Classic I Ching) ───────────────────────────────────

  /** "Openen" op een I Tjing-moment (tijdlijn of kalender): de raadpleging in de dialoog. */
  protected openConsultation(consultationId: string): void {
    this.ichingDialogTarget.set({ kind: 'view', consultationId, chartName: this.chartName() });
  }

  /** Derde keuze in de momentdialoog: een nieuwe raadpleging voor deze chart op dat tijdstip. */
  protected onIChingRequested(request: HdIChingConsultRequest): void {
    this.ichingDialogTarget.set({ kind: 'new', ...request });
  }

  private async loadIChingMarkers(chartId: string | null): Promise<void> {
    const seq = ++this.ichingLoadSeq;
    if (!chartId) {
      this.ichingMarkers.set([]);
      return;
    }
    try {
      const markers = await this.ichingMoments.markersForChart(chartId);
      if (seq === this.ichingLoadSeq) this.ichingMarkers.set(markers);
    } catch {
      // Een optionele laag: een fout laat de rest van de tijdlijn staan.
      if (seq === this.ichingLoadSeq) this.ichingMarkers.set([]);
    }
  }

  protected readonly sourcePreset = computed(() => this.chartPreset());

  private resetData(): void {
    this.error.set('');
    this.standardBlocked.set(false);
    this.chartPreset.set(null);
    this.momentMarkers.set([]);
    this.cycleMarkers.set([]);
    this.ichingMarkers.set([]);
    this.ichingLoadSeq++;
    this.stripLanes.set([]);
    this.lastViewport.set(null);
    this.stripLoadSeq++;
  }

  /**
   * Transit-strip-data (spec §2.5): gate-segmenten voor de geselecteerde
   * (trage) planeten over het zichtbare venster + 50% marge, gechunkt onder de
   * backend-cap; de TransitDataService dedupet en cachet zelf per dag/planeet.
   * Laatste aanvraag wint (seq-guard).
   */
  private async loadStripSegments(): Promise<void> {
    const viewport = this.lastViewport();
    const active = this.showTransitStrip() && this.chartId() != null && viewport != null
      && (viewport.zoom === 'year' || viewport.zoom === 'month');
    const keys = this.stripPlanetKeys();
    if (!active || keys.length === 0) {
      this.stripLanes.set([]);
      return;
    }
    const seq = ++this.stripLoadSeq;
    const marginMs = (viewport!.toMs - viewport!.fromMs) / 2;
    const fromMs = viewport!.fromMs - marginMs;
    const toMs = viewport!.toMs + marginMs;
    const chunks: { from: number; to: number }[] = [];
    for (let start = fromMs; start < toMs; start += STRIP_CHUNK_MS) {
      chunks.push({ from: start, to: Math.min(start + STRIP_CHUNK_MS, toMs) });
    }
    try {
      const results = await Promise.all(chunks.map(chunk =>
        this.transitData.getSegments(moment.utc(chunk.from), moment.utc(chunk.to), keys, 'gate')));
      if (seq !== this.stripLoadSeq) return;

      // Chunks kunnen hetzelfde (venster-overschrijdende) segment dubbel
      // leveren: dedupe per planeet op de echte segmentstart.
      const blocksByPlanet = new Map<string, Map<number, HdLifeTransitBlock>>();
      for (const result of results) {
        for (const planet of result) {
          if (!planet.planetKey) continue;
          let byStart = blocksByPlanet.get(planet.planetKey);
          if (!byStart) {
            byStart = new Map();
            blocksByPlanet.set(planet.planetKey, byStart);
          }
          for (const segment of planet.segments) {
            const startMs = segment.fromUtc.valueOf();
            if (!byStart.has(startMs)) {
              byStart.set(startMs, { fromMs: startMs, toMs: segment.toUtc.valueOf(), gate: segment.gate });
            }
          }
        }
      }
      this.stripLanes.set(keys
        .filter(key => blocksByPlanet.has(key))
        .map((key): HdLifeTransitLane => {
          const config = getHdPlanetDisplayConfig(key);
          return {
            planetKey: key,
            label: config.label,
            color: config.stripeColor,
            blocks: [...blocksByPlanet.get(key)!.values()].sort((a, b) => a.fromMs - b.fromMs),
          };
        }));
    } catch {
      if (seq === this.stripLoadSeq) this.stripLanes.set([]);
    }
  }

  private async loadChart(chartId: string): Promise<void> {
    this.loading.set(true);
    try {
      const [document, relations] = await Promise.all([
        this.library.getChartDocument(chartId),
        this.groupService.listRelations(chartId),
      ]);
      if (this.chartId() !== chartId) return;
      // Levenstijdlijn is er alleen voor standaard charts (spec §2.6); de
      // kiezer filtert al, dit vangt deeplinks naar afgeleide charts.
      const preset = document.preset;
      if (preset.cycle != null || preset.composite != null || preset.transit != null) {
        this.standardBlocked.set(true);
        return;
      }
      this.chartPreset.set(preset);

      this.momentMarkers.set(buildHdLifeTimelineMarkers(buildHdMomentRows(relations)));

      // Berekende cycli als systeem-markers; een fout hier laat de rest staan
      // (cycli zijn een optionele laag).
      try {
        const cycles = await this.cyclesService.getCycleMoments(chartId);
        if (this.chartId() !== chartId) return;
        const translate = (key: string) =>
          this.transloco.translate<string>(key, undefined, this.contentLang.effectiveLang());
        // Alle cycli, ook de Jupiter-returns tot ±107 jaar: de tijdlijn houdt de schaal
        // van het hoofdbereik en zet wat erbuiten valt ernaast (scrollen).
        this.cycleMarkers.set((cycles.cycles ?? [])
          .filter(cycle => cycle.key && cycle.primaryUtc)
          .map(cycle => ({
            // Jupiter-returns komen genummerd terug onder één key (±12, 24, 36 … jaar).
            id: cycle.returnNumber != null ? `cycle:${cycle.key}:${cycle.returnNumber}` : `cycle:${cycle.key}`,
            kind: 'cycle',
            startUtc: cycle.primaryUtc as string,
            label: cycle.returnNumber != null
              ? this.transloco.translate<string>('humanDesign.cycles.numberedReturn', {
                label: hdCycleLabel(translate, cycle.key),
                number: cycle.returnNumber,
              }, this.contentLang.effectiveLang())
              : hdCycleLabel(translate, cycle.key),
            // Solar returns dragen een zon-icoontje (finetune 2026-09-21).
            icon: cycle.key === 'solarReturn' ? 'sun' : 'arrows-rotate',
          })));
      } catch {
        this.cycleMarkers.set([]);
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }

  private async loadCandidates(): Promise<void> {
    try {
      // Server-side typefilter: alleen Standard-charts komen in aanmerking
      // voor een levenstijdlijn (spec §2.6; momentlinks blijven alle typen).
      const charts = await this.library.queryAllCharts(buildChartDataSourceQuery({
        page: 1,
        pageSize: 100,
        filterFields: [{ key: 'typeDescription', value: PlatformChartType.Standard }],
      }));
      this.candidates.set(charts);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Zichtbaar in de template voor het geboortejaar-fallback-label. */
  protected readonly birthText = computed(() => {
    const birth = this.birthUtc();
    const locale = momentLocaleFor(this.contentLang.effectiveLang());
    return birth ? moment.utc(birth).local().locale(locale).format('D MMMM YYYY') : '';
  });
}
